import { useCallback, useEffect, useRef, useState } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';

/**
 * Local-buffered, debounced-commit field. Keystrokes live in local state so the
 * input never round-trips through the store per key (no dropped letters). The
 * store commit fires after `debounceMs` of idle and on blur. Local state re-seeds
 * ONLY when `resetKey` changes (e.g. switching customer) — never mid-typing from
 * a background `value` change — and flushes the previous record's pending commit
 * on key-change / unmount.
 */
export function useAutosaveField(
  value: string,
  resetKey: string,
  onCommit: (v: string) => void,
  debounceMs = 400,
) {
  const [local, setLocal] = useState(value);
  const externalRef = useRef(value);
  externalRef.current = value;
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  const pending = useRef<null | (() => void)>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const p = pending.current;
    pending.current = null;
    p?.();
  }, []);

  // Re-seed on record change; flush the previous record's pending commit on the
  // way out (also on unmount). Reads `value` via a ref so a background value
  // change with an unchanged resetKey does NOT re-run this effect (which is what
  // would clobber in-flight typing).
  useEffect(() => {
    setLocal(externalRef.current);
    return () => flush();
  }, [resetKey, flush]);

  const onChange = useCallback(
    (v: string) => {
      setLocal(v);
      const commit = commitRef.current; // bound to the CURRENT record
      pending.current = () => commit(v);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        const p = pending.current;
        pending.current = null;
        p?.();
      }, debounceMs);
    },
    [debounceMs],
  );

  return { value: local, onChange, onBlur: flush };
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onBlur'> & {
  value: string;
  resetKey: string;
  onCommit: (v: string) => void;
  debounceMs?: number;
};

export function AutosaveTextInput({ value, resetKey, onCommit, debounceMs, ...rest }: InputProps) {
  const f = useAutosaveField(value, resetKey, onCommit, debounceMs);
  return <input {...rest} value={f.value} onChange={(e) => f.onChange(e.target.value)} onBlur={f.onBlur} />;
}

type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange' | 'onBlur'> & {
  value: string;
  resetKey: string;
  onCommit: (v: string) => void;
  debounceMs?: number;
};

export function AutosaveTextarea({ value, resetKey, onCommit, debounceMs, ...rest }: TextareaProps) {
  const f = useAutosaveField(value, resetKey, onCommit, debounceMs);
  return <textarea {...rest} value={f.value} onChange={(e) => f.onChange(e.target.value)} onBlur={f.onBlur} />;
}
